import React, { Component, PropTypes } from 'react';
import { Link } from 'react-router';
import partial from 'lodash/partial';
import classnames from 'classnames';

const DEFAULT_ITEMS = [{
  title: 'Real designs, not bids and proposals',
  icon: 'fa fa-check',
  description: 'Instead of wasting your valuable time comparing bids and proposals, you’ll pick from dozens of real designs. When your project ends, just pick the one you love!',
  question: 'How many designs can you expect?',
  answer: "You'll see dozens of designs from multiple designers. We'll let you know every time a new design or comment comes in and invite you to give feedback. The more feedback you provide, the more designs you'll receive. Years of experience show us that projects with active clients will receive more than double the entries compared to projects with low client participation."
}, {
  title: "We've got your back",
  icon: 'fa fa-check',
  description: "Get in touch with our award-winning customer support team by phone, email or chat - or get instant answers in our Help Center. We're here to ensure your success.",
  question: 'How can you get help?',
  answer: "The Help Center is full of helpful tips and tricks, guides, and articles to help you with your project. You can chat live with our team - just call us weekdays during business hours at 877.887.7442. Or you can send us a note and we'll respond right away. We created an award-winning platform that's intuitive and easy to use, but just in case you have a question or run into an issue, we're here to help."
}, {
  title: "Experienced designers who deliver results",
  icon: 'fa fa-check',
  description: "A global community of 220,000+ talented designers who can help turn your customers and prospects into loyal fans in as little as 1 to 7 days.",
  question: 'Can you ask designers to make changes?',
  answer: "<p>Absolutely! In fact, this is a key factor for success. Once the designs start rolling in, let the designers know what you think about their designs. You can score and give individual feedback. You can also update your brief if you want to address all participants in your project. They are happy to make changes and iterate on their designs.</p><p>When you score each entry (1 to 5 stars), your score and comments are private. Only you and the individual designer will see the star rating and comments on their design. Updates to your brief are public for all participants in the project.</p>"

}, {
  title: "Powerful intellectual property protections",
  icon: 'fa fa-check',
  description: "NDA and IP transfer agreements, private galleries and more. For over a decade, crowdspring is the design and naming service trusted by businesses and agencies.",
  question: 'What will you own when the project is over?',
  answer: "<p>Every project on crowdspring is protected by a custom legal contract giving you full rights to the work you're buying. If you have multiple awards, you'll have separate contracts for each. Once the contract is signed and the creative is paid after you approve the final deliverables in your project, you can use the design any way you like. You can change it or alter it in any way – it's yours.</p><p>The non-winning work remains the property of the person who created that work. This means you may not use those other entries (or any portions of them) in any way. But we give you an easy way to add awards and make offers in your projects when you find more than one design you love.</p>"
}, {
  title: "Powerful tools that save you time",
  icon: 'fa fa-check',
  description: "Easy-to-use, award-winning tools that speed up the design process. From project management to focus groups to collaboration, IP contracts, and more.",
  question: 'Can you ask others to help you choose?',
  answer: "<p>Yes! Before making the final decision, you may want to run a free focus group. All  design packages include unlimited focus groups.</p><p>A focus group (also called a \"poll\") allows you to invite friends, customers, colleagues and anyone else to vote and comment on their favorite designs (you can assign weighted votes or let each vote count equally). Anyone is eligible to vote as long as they have the special voting URL we'll provide to you (they will not need to register an account). Your focus group can be public or private - you choose! You fully control whom you invite to each focus group and what designs are included in each focus group.</p>"
}, {
  title: "Work you'll love, guaranteed",
  icon: 'fa fa-check',
  description: "We hate confusing refund policies, so ours is simple: if you're not 100% happy with your design project, contact us within 30 days and we'll refund what you paid.",
  question: 'Why does crowdspring offer a guarantee?',
  answer: "We have one of the most talented communities of designers on the planet. Over the past decade, our team has helped tens of thousands of small business owners, entrepreneurs, agencies, and non-profits. The vast majority of our clients find a great design they love. But we know that sometimes, you just won't find something you love. It's pretty rare, but it happens. That's why we offer your a simple promise: you'll find something you love, or your money back."
}, {
  title: "Manage your project anytime, anywhere",
  icon: 'fa fa-check',
  description: "In the office, from your couch, or on-the-go, crowdspring's powerful platform is accessible from computers, tablets and smartphones.",
  question: 'Who sees what?',
  answer: "Want to keep things on the down low? We offer privacy settings like search engine exclusion, non-disclosure agreements (NDA's), and private galleries so only you can see the designs."
}, {
  title: "Save thousands today, more tomorrow",
  icon: 'fa fa-check',
  description: "Save thousands of dollars compared to the competition. Beautiful design powerful enough for agencies & Fortune 500 companies at a fair price for every budget.",
  question: 'How much does it cost to post a project?',
  answer: "Crowd packages start at $299 in most categories, while projects with a single designer (1-to-1 projects) start at $200. All packages include the award to the designer and crowdspring's fee, a custom legal contract giving you full rights to the work you're buying, free focus groups (design projects only), our award-winning customer support, tools and platform. You can select packages and features based on what you need and your budget. You will always know up front exactly what you'll pay, with no hidden costs and no surprises."
}];

export default class Differentiators extends Component {
  constructor(props) {
    super(props);

    this.state = { toggledQuestions: [] };
    this.toggleQuestion = this.toggleQuestion.bind(this);
  }

  toggleQuestion(i) {
    const toggledQuestions = [...this.state.toggledQuestions];
    toggledQuestions[i] = !toggledQuestions[i];
    this.setState({ toggledQuestions });
  }

  render() {
    const { toggledQuestions } = this.state;
    const { items, title, subtitle, background, cta } = this.props;

    return (
      <div className={classnames("block block-differentiators", background)}>
        <div className="row columns">
          <div className="header">
            <h2>{ title || "Price is what you pay. Value is what you get." }</h2>
            <h3>
              { subtitle ||
              <span>
                Not everyone has the same needs, so we created flexible features for every
                budget.<br/>Crowdspring delivers great results and unmatched value.
              </span> }
            </h3>
          </div>
        </div>
        <div className="row">
          { (items || DEFAULT_ITEMS).map((item, i) => (
          <div className="columns small-16 medium-8 item" key={i}>
            <div className="row">
              <div className="columns small-16 large-2 icon-column">
                <i className={item.icon} />
              </div>
              <div className="columns small-16 large-14">
                <h4 className="as-h3-important">{ item.title }</h4>
                <p>{ item.description }</p>
                <a onClick={partial(this.toggleQuestion, i)}>
                  { item.question }
                  <i className={"pull-right fa fa-" + (toggledQuestions[i]? "minus" : "plus")} />
                </a>
                { toggledQuestions[i] && <p dangerouslySetInnerHTML={{ __html: item.answer }} /> }
              </div>
            </div>
          </div>
          )) }
        </div>

        { !!cta &&
        <div className="as-h3-important text-center cta">
          <Link to={cta.link}>{ cta.label }</Link>
        </div> }
      </div>
    );
  }
}

Differentiators.propTypes = {
  background: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  items: PropTypes.array,
  cta: PropTypes.object
};
